/**
 * PAGE: ADMIN — Numéros d'équipage orphelins (TICKET-CRM-07).
 *
 * Liste les équipages qui portent un crew_number alloué au clic « Payer »
 * (PAY-10) mais sans aucun paiement BPI encaissé. Permet de libérer le numéro.
 *
 * RÈGLE MÉTIER : libérer = UPDATE crews SET crew_number = NULL. L'équipage reste
 * actif (re-paiement possible plus tard) et sort de la vue (plus de numéro).
 *
 * Sécurité réelle = vue (filtre current_user_role + security_invoker) + RPC
 * (current_user_role). Le guard useCanAccessUsers ci-dessous est purement UX.
 */
import { useState } from 'react';
import { Hash } from 'lucide-react';
import { PageHeader, PageLayout } from '../components/page';
import { ErrorState, LoadingState, toast } from '../components/ui';
import { ReleaseOrphanCrewNumberDialog } from '../components/modals/ReleaseOrphanCrewNumberDialog';
import { useOrphanCrewNumbers } from '../hooks/useOrphanCrewNumbers';
import { useCanAccessUsers } from '../hooks/useCanAccessUsers';
import { memberLabel, releaseOrphanErrorMessage } from '../lib/orphanCrewNumberErrors';
import type { OrphanCrewNumber } from '@/types/orphanCrewNumber';

const BREADCRUMBS = [{ label: 'Admin' }, { label: "Numéros d'équipage orphelins" }];

export function OrphanCrewNumbersPage() {
  const canAccess = useCanAccessUsers();
  const { orphans, loading, error, releaseOrphanCrewNumber } = useOrphanCrewNumbers();
  const [selected, setSelected] = useState<OrphanCrewNumber | null>(null);

  if (!canAccess) {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <ErrorState
          title="Accès réservé"
          message="Cette page est réservée aux administrateurs."
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <LoadingState message="Chargement des numéros orphelins..." />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
        <ErrorState
          title="Erreur lors du chargement"
          message={error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  const handleConfirm = async (crewId: string, reason: string) => {
    try {
      await releaseOrphanCrewNumber(crewId, reason);
      toast.success('Numéro libéré');
      setSelected(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Opération impossible';
      toast.error('Échec', { description: releaseOrphanErrorMessage(message) });
    }
  };

  return (
    <PageLayout header={<PageHeader breadcrumbs={BREADCRUMBS} />}>
      <div className="h-px w-full bg-zinc-200" />

      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Numéros d'équipage orphelins</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Équipages dont le numéro a été attribué au moment du paiement mais sans encaissement
            BPI. Libérer le numéro le rend disponible : l'équipage reste actif et pourra relancer un
            paiement.
          </p>
        </div>

        {orphans.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-white py-16">
            <Hash className="size-10 text-zinc-300" />
            <p className="text-sm text-zinc-600">Aucun numéro orphelin 🎉</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    N°
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Produit
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Pilote
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Copilote
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Dernier statut
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Âge
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((orphan) => (
                  <tr
                    key={orphan.crew_id}
                    className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-mono font-medium text-zinc-900">
                      {orphan.crew_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-700">
                      {orphan.product_name}
                      {orphan.millesime ? ` ${orphan.millesime}` : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-700">{memberLabel(orphan.pilot)}</td>
                    <td className="px-4 py-3 text-sm text-zinc-700">
                      {memberLabel(orphan.copilot)}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500">
                      {orphan.latest_payment_status ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-zinc-500">
                      {orphan.age_days} j
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setSelected(orphan)}
                        className="inline-flex items-center rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                      >
                        Libérer le numéro
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReleaseOrphanCrewNumberDialog
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        orphan={selected}
        onConfirm={handleConfirm}
      />
    </PageLayout>
  );
}
